'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

const serviceLinks = [
{ label: 'Into23 Data+', href: '/services/data-plus', desc: 'AI training data, annotation & evaluation' },
{ label: 'Into23 Verify+', href: '/services/verify-plus', desc: 'AI translation with human verification' },
{ label: 'Into23 Voice+', href: '/services/voice-plus', desc: 'Multilingual AI voiceovers & eLearning' }];


const navLinks = [
{ label: 'Home', href: '/' },
{ label: 'About', href: '/about' },
{ label: 'Blog', href: '/blog' },
{ label: 'FAQ', href: '/faq' },
{ label: 'Client Portal', href: 'https://workspace.into23.com', external: true },
{ label: 'Resources', href: '/resources' },
{ label: 'Contact', href: '/contact' }];


export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const pathname = usePathname();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname?.startsWith(href);
  };

  const isServicesActive = serviceLinks.some((s) => pathname?.startsWith(s.href));

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-obsidian/95 backdrop-blur-md border-b border-steel/20' : 'bg-transparent'}`
      }>
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/">
            <Image
              alt="Into23"
              src="/assets/images/Into23_NewLogo-1780324533733.png"
              width={120}
              height={40}
              className="h-8 lg:h-10 w-auto object-contain hover:opacity-90 transition-opacity" />
            
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Home link first */}
            {(() => {
              const homeLink = navLinks.find((l) => l.href === '/');
              const active = isActive(homeLink!.href);
              return (
                <Link key={homeLink!.href} href={homeLink!.href}>
                  <div
                    className="relative px-4 py-2 text-sm font-medium transition-colors rounded-lg"
                    style={{ color: active ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}>
                    
                    {homeLink!.label}
                    {active &&
                    <div
                      className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                      style={{ backgroundColor: 'rgb(43, 98, 239)', opacity: 1 }} />

                    }
                  </div>
                </Link>);

            })()}

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                className="relative px-4 py-2 text-sm font-medium transition-colors rounded-lg flex items-center gap-1"
                style={{ color: isServicesActive ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
                onClick={() => setServicesOpen(!servicesOpen)}>
                
                Services
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`}>
                  <path d="m6 9 6 6 6-6"></path>
                </svg>
                {isServicesActive &&
                <div
                  className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                  style={{ backgroundColor: 'rgb(43, 98, 239)', opacity: 1 }} />

                }
              </button>

              {/* Dropdown Menu */}
              {servicesOpen &&
              <div
                className="absolute top-full left-0 mt-1 w-72 rounded-xl border shadow-2xl py-2"
                style={{ backgroundColor: 'rgb(13, 16, 23)', borderColor: 'rgba(31, 34, 39, 0.6)' }}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}>
                
                  {serviceLinks.map((svc) =>
                <Link key={svc.href} href={svc.href} onClick={() => setServicesOpen(false)}>
                      <div
                    className="px-4 py-3 mx-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: isActive(svc.href) ? 'rgba(43, 98, 239, 0.1)' : 'transparent'
                    }}>
                    
                        <div className="text-sm font-semibold mb-0.5" style={{ color: isActive(svc.href) ? 'rgb(85, 136, 251)' : 'oklch(95% .005 265)' }}>
                          {svc.label}
                        </div>
                        <div className="text-xs" style={{ color: 'oklch(65% .01 265)' }}>{svc.desc}</div>
                      </div>
                    </Link>
                )}
                  <div className="mx-4 mt-2 pt-2 border-t" style={{ borderColor: 'rgba(31, 34, 39, 0.3)' }}>
                    <Link href="/services" onClick={() => setServicesOpen(false)}>
                      <div className="text-xs font-medium py-1" style={{ color: 'oklch(65% .01 265)' }}>
                        View all services →
                      </div>
                    </Link>
                  </div>
                </div>
              }
            </div>

            {navLinks?.filter((link) => link.href !== '/').map((link) => {
              const active = isActive(link?.href);
              const isExternal = (link as any)?.external;
              const linkContent = (
                <div
                  className="relative px-4 py-2 text-sm font-medium transition-colors rounded-lg"
                  style={{ color: active ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}>
                    
                    {link?.label}
                    {active &&
                    <div
                      className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full"
                      style={{ backgroundColor: 'rgb(43, 98, 239)', opacity: 1 }} />

                    }
                  </div>
              );
              if (isExternal) {
                return (
                  <a key={link?.href} href={link?.href} target="_blank" rel="noopener noreferrer">
                    {linkContent}
                  </a>
                );
              }
              return (
                <Link key={link?.href} href={link?.href}>
                  {linkContent}
                </Link>);

            })}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Language switcher placeholder */}
            <div className="hidden lg:block">
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors"
                style={{
                  color: 'oklch(65% .01 265)',
                  borderColor: 'rgba(31, 34, 39, 0.3)',
                  backgroundColor: 'rgba(19, 22, 28, 0.3)'
                }}>
                
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                  <path d="M2 12h20"></path>
                </svg>
                <span className="hidden sm:inline">English</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m6 9 6 6 6-6"></path>
                </svg>
              </button>
            </div>

            {/* CTA Button */}
            <Link href="/get-a-quote">
              <div
                className="hidden sm:flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg transition-all"
                style={{ backgroundColor: 'rgb(43, 98, 239)' }}>
                
                Get a Quote
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6"></path>
                </svg>
              </div>
            </Link>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 transition-colors"
              style={{ color: 'oklch(65% .01 265)' }}
              onClick={() => setMobileOpen(!mobileOpen)}>
              
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" x2="20" y1="12" y2="12"></line>
                <line x1="4" x2="20" y1="6" y2="6"></line>
                <line x1="4" x2="20" y1="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen &&
        <div
          className="lg:hidden py-4 border-t"
          style={{ borderColor: 'rgba(31, 34, 39, 0.3)', backgroundColor: 'rgb(8, 11, 18)' }}>
          
            {/* Services accordion */}
            <button
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors"
            style={{ color: isServicesActive ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}>
            
              Services
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}>
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>
            {mobileServicesOpen &&
          <div className="pl-4 pb-2">
                {serviceLinks.map((svc) =>
            <Link key={svc.href} href={svc.href}>
                    <div
                className="block px-4 py-2.5 text-sm font-medium transition-colors"
                style={{ color: isActive(svc.href) ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}
                onClick={() => setMobileOpen(false)}>
                
                      {svc.label}
                    </div>
                  </Link>
            )}
                <Link href="/services">
                  <div
                className="block px-4 py-2.5 text-xs font-medium transition-colors"
                style={{ color: 'oklch(50% .01 265)' }}
                onClick={() => setMobileOpen(false)}>
                
                    View all services →
                  </div>
                </Link>
              </div>
          }

            {navLinks?.filter((link) => link.href !== '/').map((link) => {
          const isExternal = (link as any)?.external;
          const mobileLinkContent = (
                <div
              className="block px-4 py-3 text-sm font-medium transition-colors"
              style={{ color: isActive(link?.href) ? 'rgb(85, 136, 251)' : 'oklch(65% .01 265)' }}
              onClick={() => setMobileOpen(false)}>
              
                  {link?.label}
                </div>
          );
          if (isExternal) {
            return (
              <a key={link?.href} href={link?.href} target="_blank" rel="noopener noreferrer">
                {mobileLinkContent}
              </a>
            );
          }
          return (
          <Link key={link?.href} href={link?.href}>
                {mobileLinkContent}
              </Link>
          );
          })}
            <div className="px-4 pt-3">
              <Link href="/get-a-quote">
                <div
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-lg"
                style={{ backgroundColor: 'rgb(43, 98, 239)' }}>
                
                  Get a Quote
                </div>
              </Link>
            </div>
          </div>
        }
      </div>
    </nav>);

}